import { useEffect, useState } from "react";
import type { ToolCall } from "../../types/chat";
import { useUIStore } from "../../store/uiStore";
import { chatPalette } from "../../lib/chatTheme";

function formatElapsed(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  return s < 10 ? `${s.toFixed(1)}s` : `${Math.round(s)}s`;
}

function useElapsed(call: ToolCall): string | null {
  // Done: stable elapsed = endedAt - startedAt
  // Pending: live elapsed updated every 500ms
  const [, tick] = useState(0);
  useEffect(() => {
    if (call.status !== "pending" || !call.startedAt) return;
    const id = setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(id);
  }, [call.status, call.startedAt]);

  if (call.startedAt == null) return null;
  const end = call.endedAt ?? Date.now();
  return formatElapsed(end - call.startedAt);
}

/** Map tool name → user-facing agent label + icon + accent color. */
function agentBadge(name: string): { label: string; icon: string; color: string } {
  switch (name) {
    case "run_equity_analysis":
      return { label: "Equity Agent", icon: "⚖️", color: "#7c3aed" };
    case "run_visual_analysis":
      return { label: "VLM Agent", icon: "🛰️", color: "#0891b2" };
    case "get_cell_risk":
      return { label: "Cell Risk lookup", icon: "📍", color: "#374151" };
    case "get_weather_trend":
      return { label: "Weather lookup", icon: "🌦️", color: "#374151" };
    case "get_nearby_firms":
      return { label: "FIRMS lookup", icon: "🔥", color: "#374151" };
    // Inner trace tools (called by Equity / VLM agents)
    case "lookup_cell_demographics":
      return { label: "lookup demographics", icon: "·", color: "#9ca3af" };
    case "get_stratum_metrics":
      return { label: "lookup stratum recall", icon: "·", color: "#9ca3af" };
    case "flag_disparity":
      return { label: "test disparity", icon: "·", color: "#9ca3af" };
    case "get_tile_metadata":
      return { label: "tile metadata", icon: "·", color: "#9ca3af" };
    case "get_firms_summary":
      return { label: "FIRMS summary", icon: "·", color: "#9ca3af" };
    case "check_imagery_reliability":
      return { label: "imagery reliability", icon: "·", color: "#9ca3af" };
    default:
      return { label: name, icon: "·", color: "#9ca3af" };
  }
}

function summarize(name: string, result: Record<string, unknown> | undefined): string {
  if (!result) return "";
  if ("error" in result && result.error) return `error: ${String(result.error).slice(0, 100)}`;

  if (name === "run_equity_analysis") {
    const r = result as { svi_quintile?: string; stratum_recall?: number; is_flagged?: boolean; delta_eo_global?: number };
    const parts = [];
    if (r.svi_quintile) parts.push(`SVI ${r.svi_quintile}`);
    if (r.stratum_recall != null) parts.push(`recall ${(r.stratum_recall * 100).toFixed(1)}%`);
    if (r.delta_eo_global != null) parts.push(`ΔEO ${r.delta_eo_global.toFixed(3)}`);
    if (r.is_flagged) parts.push("🚩 flagged");
    return parts.join(" · ");
  }
  if (name === "run_visual_analysis") {
    const r = result as { fuel_state?: string; wui_indicator?: string; active_fire_visible?: boolean; insufficient_imagery?: boolean };
    if (r.insufficient_imagery) return "imagery insufficient";
    const parts = [];
    if (r.fuel_state) parts.push(`fuel: ${r.fuel_state}`);
    if (r.wui_indicator) parts.push(`WUI: ${r.wui_indicator}`);
    if (r.active_fire_visible) parts.push("🔥 active fire");
    return parts.join(" · ");
  }
  if (name === "get_cell_risk") {
    const r = result as { p_hat?: number; p_tilde?: number };
    return `p̂=${r.p_hat?.toFixed?.(3) ?? "?"} · p̃=${r.p_tilde?.toFixed?.(3) ?? "?"}`;
  }
  if (name === "get_nearby_firms") {
    const r = result as { count?: number; nearest_km?: number };
    return `${r.count ?? 0} detections · nearest ${r.nearest_km ?? "?"} km`;
  }
  if (name === "lookup_cell_demographics") {
    const r = result as { svi_quintile?: string; theme_quintiles?: Record<string, string> };
    const dom = r.theme_quintiles
      ? Object.entries(r.theme_quintiles).reduce<[string, string] | null>((acc, [k, v]) =>
          !acc || (Math.abs(Number(v.replace("Q", "")) - 3) > Math.abs(Number(acc[1].replace("Q", "")) - 3))
            ? [k, v] : acc, null)
      : null;
    return [
      r.svi_quintile && `SVI ${r.svi_quintile}`,
      dom && `dom ${dom[0]}=${dom[1]}`,
    ].filter(Boolean).join(" · ");
  }
  if (name === "get_stratum_metrics") {
    const r = result as { recall?: number; n_quintile?: number };
    return `recall ${((r.recall ?? 0) * 100).toFixed(1)}% (n=${r.n_quintile ?? "?"})`;
  }
  if (name === "flag_disparity") {
    const r = result as { flagged?: boolean; gates_passed?: string[] };
    return r.flagged ? `🚩 ${(r.gates_passed ?? []).length} gates passed` : "not flagged";
  }
  return "";
}

interface BubbleProps {
  call: ToolCall;
  depth?: number;
}

export function ToolCallBubble({ call, depth = 0 }: BubbleProps) {
  const theme = useUIStore((s) => s.theme);
  const palette = chatPalette(theme);
  const innerTrace = (call.result?.tool_call_trace ?? []) as ToolCall[];
  const summary = summarize(call.name, call.result);
  const badge = agentBadge(call.name);
  const isPending = call.status === "pending";
  const isInner = depth > 0;
  const elapsed = useElapsed(call);

  // Outer bubble (Report Agent's tool call): bold, colored, full agent label.
  // Inner bubble (within Equity/VLM trace): muted, hierarchical, short label.
  const isAgent = call.name.startsWith("run_") || call.name.startsWith("get_");

  return (
    <div
      data-testid="tool-call-bubble"
      style={{
        marginLeft: depth * 14,
        marginTop: 6,
        padding: isInner ? "3px 8px" : "6px 10px",
        background: isInner ? "transparent" : palette.toolBubbleBg,
        borderLeft: isInner ? `2px solid ${badge.color}40` : "none",
        border: isInner ? "none" : `1px solid ${palette.toolBubbleBorder}`,
        borderRadius: isInner ? 0 : 6,
        fontSize: isInner ? 11 : 12,
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
        color: palette.toolBubbleText,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span aria-hidden style={{ fontSize: isInner ? 10 : 14 }}>
          {isPending ? "⏳" : badge.icon}
        </span>
        <span style={{ fontWeight: isAgent && !isInner ? 700 : 500, color: badge.color }}>
          {badge.label}
        </span>
        {isPending ? (
          <span style={{ color: palette.metaText, fontStyle: "italic", marginLeft: 2 }}>
            thinking…{elapsed ? ` ${elapsed}` : ""}
          </span>
        ) : (
          <span style={{ color: "#10b981", fontSize: 11 }}>
            ·done{elapsed ? ` · ${elapsed}` : ""}
          </span>
        )}
      </div>
      {summary && (
        <div style={{ marginLeft: 22, color: palette.toolBubbleText, marginTop: 2 }}>
          {summary}
        </div>
      )}
      {!isInner && call.result && (
        <details style={{ marginLeft: 22, marginTop: 4 }}>
          <summary style={{ cursor: "pointer", color: theme === "dark" ? "#a5b4fc" : "#6366f1", fontSize: 11 }}>
            view raw payload
          </summary>
          <pre
            style={{
              fontSize: 10,
              background: palette.listBg,
              color: palette.toolBubbleText,
              padding: 6,
              borderRadius: 4,
              maxHeight: 160,
              overflow: "auto",
              border: `1px solid ${palette.toolBubbleBorder}`,
            }}
          >
            {JSON.stringify(call.result, null, 2)}
          </pre>
        </details>
      )}
      {innerTrace.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {innerTrace.map((inner, i) => (
            <ToolCallBubble key={`${inner.name}-${i}`} call={inner} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
